import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { notificationChannels, globalNotificationToggles, radioNotifications, programNotifications, hostNotifications, type RadioNotificationConfig, type ProgramNotificationConfig, type HostNotificationConfig } from "@/mocks/smartNotifications";
import Swal from "sweetalert2";

type ConfigSection = "global" | "radios" | "programs" | "hosts";

export default function NotificacionesInteligentesPage() {
  const navigate = useNavigate();
  const [section, setSection] = useState<ConfigSection>("global");
  const [channels, setChannels] = useState(notificationChannels);
  const [toggles, setToggles] = useState(globalNotificationToggles);
  const [radioConfigs, setRadioConfigs] = useState(radioNotifications);
  const [programConfigs, setProgramConfigs] = useState(programNotifications);
  const [hostConfigs, setHostConfigs] = useState(hostNotifications);

  const toggleChannel = useCallback((id: string) => {
    setChannels((prev) => prev.map((c) => c.id === id ? { ...c, enabled: !c.enabled } : c));
  }, []);

  const toggleGlobal = useCallback((id: string) => {
    setToggles((prev) => prev.map((t) => t.id === id ? { ...t, enabled: !t.enabled } : t));
    Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Preferencia actualizada", showConfirmButton: false, timer: 1200, timerProgressBar: true });
  }, []);

  const toggleRadioConfig = useCallback((radioId: number, field: keyof RadioNotificationConfig) => {
    setRadioConfigs((prev) => prev.map((r) => r.radioId === radioId ? { ...r, [field]: !r[field] } : r));
  }, []);

  const toggleProgramConfig = useCallback((programId: number, field: keyof ProgramNotificationConfig) => {
    setProgramConfigs((prev) => prev.map((p) => p.programId === programId ? { ...p, [field]: !p[field] } : p));
  }, []);

  const toggleHostConfig = useCallback((hostId: number, field: keyof HostNotificationConfig) => {
    setHostConfigs((prev) => prev.map((h) => h.hostId === hostId ? { ...h, [field]: !h[field] } : h));
  }, []);

  const sections: { id: ConfigSection; label: string; icon: string; color: string }[] = [
    { id: "global", label: "General", icon: "ri-settings-3-line", color: "text-foreground-600" },
    { id: "radios", label: "Por Radio", icon: "ri-radio-line", color: "text-rose-500" },
    { id: "programs", label: "Por Programa", icon: "ri-mic-line", color: "text-emerald-500" },
    { id: "hosts", label: "Por Locutor", icon: "ri-user-voice-line", color: "text-amber-500" },
  ];

  return (
    <div className="fixed inset-0 flex flex-col bg-background-50 overflow-hidden">
      {/* Header */}
      <div className="shrink-0 px-4 md:px-6 pt-4 pb-2 border-b border-background-100">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate("/app")} className="w-9 h-9 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer shrink-0 transition-all">
            <i className="ri-arrow-left-line text-foreground-600"></i>
          </button>
          <div>
            <h1 className="text-lg font-bold text-foreground-950">Notificaciones</h1>
            <p className="text-[11px] text-foreground-400">Configurá qué querés recibir y de quién</p>
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                section === s.id
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-100 text-foreground-500 hover:bg-background-200"
              }`}
            >
              <i className={`${s.icon} ${section === s.id ? "text-background-50" : s.color} text-[13px]`}></i>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-none px-4 md:px-6 py-4">
        {/* Canales */}
        <div className="mb-5">
          <h3 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-2.5">Canales de Notificación</h3>
          <div className="space-y-1.5">
            {channels.map((ch) => (
              <div key={ch.id} className="flex items-center justify-between bg-background-50 border border-background-200 rounded-2xl p-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center shrink-0">
                    <i className={`${ch.icon} text-foreground-600 text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground-950">{ch.label}</p>
                    <p className="text-[11px] text-foreground-500">{ch.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleChannel(ch.id)}
                  className={`relative w-12 h-7 rounded-full transition-all cursor-pointer shrink-0 ${
                    ch.enabled ? "bg-primary-500" : "bg-background-300"
                  }`}
                >
                  <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
                    ch.enabled ? "translate-x-[22px]" : "translate-x-[2px]"
                  }`}></div>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Global toggles */}
        {section === "global" && (
          <div>
            <h3 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-2.5">Tipos de Notificación</h3>
            <div className="space-y-1.5">
              {toggles.map((t) => (
                <div key={t.id} className="flex items-center justify-between bg-background-50 border border-background-200 rounded-2xl p-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center shrink-0">
                      <i className={`${t.icon} ${t.color} text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground-950">{t.label}</p>
                      <p className="text-[11px] text-foreground-500">{t.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleGlobal(t.id)}
                    className={`relative w-12 h-7 rounded-full transition-all cursor-pointer shrink-0 ${
                      t.enabled ? "bg-primary-500" : "bg-background-300"
                    }`}
                  >
                    <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
                      t.enabled ? "translate-x-[22px]" : "translate-x-[2px]"
                    }`}></div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Radios */}
        {section === "radios" && (
          <div>
            <h3 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-2.5">Configuración por Radio</h3>
            <div className="space-y-2">
              {radioConfigs.map((radio) => (
                <div key={radio.radioId} className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                  {/* Radio header */}
                  <div className="flex items-center gap-3 p-3.5 border-b border-background-100">
                    <div className="w-10 h-10 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
                      <img src={radio.radioImage} alt={radio.radioName} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-bold text-foreground-950">{radio.radioName}</h4>
                      <p className="text-[10px] text-foreground-500">{radio.radioFrequency}</p>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-foreground-400">
                      {(radio.liveStarted ? 1 : 0) + (radio.newEvent ? 1 : 0) + (radio.newContest ? 1 : 0)}/3 activos
                    </div>
                  </div>
                  {/* Toggles */}
                  <div className="p-3 space-y-2">
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-xs text-foreground-700">
                        <i className="ri-broadcast-line text-rose-500 mr-1.5"></i>Radio en Vivo
                      </span>
                      <button
                        onClick={() => toggleRadioConfig(radio.radioId, "liveStarted")}
                        className={`relative w-11 h-6 rounded-full transition-all cursor-pointer shrink-0 ${
                          radio.liveStarted ? "bg-primary-500" : "bg-background-300"
                        }`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                          radio.liveStarted ? "translate-x-[22px]" : "translate-x-[2px]"
                        }`}></div>
                      </button>
                    </label>
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-xs text-foreground-700">
                        <i className="ri-calendar-event-line text-violet-500 mr-1.5"></i>Nuevos Eventos
                      </span>
                      <button
                        onClick={() => toggleRadioConfig(radio.radioId, "newEvent")}
                        className={`relative w-11 h-6 rounded-full transition-all cursor-pointer shrink-0 ${
                          radio.newEvent ? "bg-primary-500" : "bg-background-300"
                        }`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                          radio.newEvent ? "translate-x-[22px]" : "translate-x-[2px]"
                        }`}></div>
                      </button>
                    </label>
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-xs text-foreground-700">
                        <i className="ri-trophy-line text-orange-500 mr-1.5"></i>Nuevos Concursos
                      </span>
                      <button
                        onClick={() => toggleRadioConfig(radio.radioId, "newContest")}
                        className={`relative w-11 h-6 rounded-full transition-all cursor-pointer shrink-0 ${
                          radio.newContest ? "bg-primary-500" : "bg-background-300"
                        }`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                          radio.newContest ? "translate-x-[22px]" : "translate-x-[2px]"
                        }`}></div>
                      </button>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Programas */}
        {section === "programs" && (
          <div>
            <h3 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-2.5">Configuración por Programa</h3>
            <div className="space-y-2">
              {programConfigs.map((prog) => (
                <div key={prog.programId} className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                  <div className="flex items-center gap-3 p-3.5 border-b border-background-100">
                    <div className="w-10 h-10 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
                      <img src={prog.programImage} alt={prog.programName} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-bold text-foreground-950">{prog.programName}</h4>
                      <p className="text-[10px] text-foreground-500">{prog.radioName}</p>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-foreground-400">
                      {(prog.programStarting ? 1 : 0) + (prog.newEpisode ? 1 : 0)}/2 activos
                    </div>
                  </div>
                  <div className="p-3 space-y-2">
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-xs text-foreground-700">
                        <i className="ri-timer-line text-emerald-500 mr-1.5"></i>Programa por Comenzar
                      </span>
                      <button
                        onClick={() => toggleProgramConfig(prog.programId, "programStarting")}
                        className={`relative w-11 h-6 rounded-full transition-all cursor-pointer shrink-0 ${
                          prog.programStarting ? "bg-primary-500" : "bg-background-300"
                        }`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                          prog.programStarting ? "translate-x-[22px]" : "translate-x-[2px]"
                        }`}></div>
                      </button>
                    </label>
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-xs text-foreground-700">
                        <i className="ri-play-list-line text-emerald-500 mr-1.5"></i>Nuevo Episodio
                      </span>
                      <button
                        onClick={() => toggleProgramConfig(prog.programId, "newEpisode")}
                        className={`relative w-11 h-6 rounded-full transition-all cursor-pointer shrink-0 ${
                          prog.newEpisode ? "bg-primary-500" : "bg-background-300"
                        }`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                          prog.newEpisode ? "translate-x-[22px]" : "translate-x-[2px]"
                        }`}></div>
                      </button>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Locutores */}
        {section === "hosts" && (
          <div>
            <h3 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-2.5">Configuración por Locutor</h3>
            <div className="space-y-2">
              {hostConfigs.map((host) => (
                <div key={host.hostId} className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                  <div className="flex items-center gap-3 p-3.5 border-b border-background-100">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-100 border border-background-200 shrink-0">
                      <img src={host.hostImage} alt={host.hostName} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-bold text-foreground-950">{host.hostName}</h4>
                      <p className="text-[10px] text-foreground-500">{host.radioName}</p>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-foreground-400">
                      {(host.hostLiveStarted ? 1 : 0) + (host.newPost ? 1 : 0)}/2 activos
                    </div>
                  </div>
                  <div className="p-3 space-y-2">
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-xs text-foreground-700">
                        <i className="ri-user-voice-line text-amber-500 mr.1.5"></i>Locutor al Aire
                      </span>
                      <button
                        onClick={() => toggleHostConfig(host.hostId, "hostLiveStarted")}
                        className={`relative w-11 h-6 rounded-full transition-all cursor-pointer shrink-0 ${
                          host.hostLiveStarted ? "bg-primary-500" : "bg-background-300"
                        }`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                          host.hostLiveStarted ? "translate-x-[22px]" : "translate-x-[2px]"
                        }`}></div>
                      </button>
                    </label>
                    <label className="flex items-center justify-between cursor-pointer">
                      <span className="text-xs text-foreground-700">
                        <i className="ri-chat-3-line text-amber-500 mr-1.5"></i>Nueva Publicación
                      </span>
                      <button
                        onClick={() => toggleHostConfig(host.hostId, "newPost")}
                        className={`relative w-11 h-6 rounded-full transition-all cursor-pointer shrink-0 ${
                          host.newPost ? "bg-primary-500" : "bg-background-300"
                        }`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                          host.newPost ? "translate-x-[22px]" : "translate-x-[2px]"
                        }`}></div>
                      </button>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Save button */}
      <div className="shrink-0 px-4 md:px-6 py-4 border-t border-background-100">
        <button
          onClick={() => {
            Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Preferencias guardadas", showConfirmButton: false, timer: 1500, timerProgressBar: true });
          }}
          className="w-full py-3 rounded-xl bg-primary-500 hover:bg-primary-600 text-background-50 font-bold text-sm cursor-pointer whitespace-nowrap transition-all"
        >
          <i className="ri-check-line mr-1.5"></i>
          Guardar Preferencias
        </button>
      </div>
    </div>
  );
}